/**
 * DRR-317: Auth Observability Hook
 *
 * Provides access to auth health check, drift detection, and login simulation.
 * Used by the admin dashboard to display auth state.
 */

import { useQuery, useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';

// Queries
const AUTH_HEALTH_CHECK = gql`
  query GetAuthHealthCheck {
    authHealthCheck {
      passed
      totalAssertions
      failures {
        roleId
        message
      }
      checkedAt
    }
  }
`;

const AUTH_DRIFT_STATUS = gql`
  query GetAuthDriftStatus {
    authDriftStatus {
      hasDrift
      baselinePath
      removed {
        roleId
        scope
      }
      added {
        roleId
        scope
      }
      changed
    }
  }
`;

const SIMULATE_LOGIN = gql`
  query SimulateLogin($email: String!) {
    simulateLogin(email: $email) {
      email
      roleId
      tenantSlug
      tenantNum
      baseScopeCount
      overrideScopeCount
      overrideId
      totalScopeCount
      effectiveScopes
      capabilityChecks {
        subgraph
        entity
        shouldHaveAccess
        passed
      }
      allPassed
    }
  }
`;

const LIST_PERSONAS = gql`
  query ListPersonas {
    listPersonas {
      email
      role
      tenant
    }
  }
`;

// Mutations
const GENERATE_SNAPSHOT = gql`
  mutation GenerateAuthSnapshot {
    generateAuthSnapshot {
      snapshotId
      generatedAt
      gitCommit
      roleCount
      scopeRuleCount
      roleOverrideCount
      path
    }
  }
`;

const SET_BASELINE = gql`
  mutation SetAuthBaseline($snapshotId: String!) {
    setAuthBaseline(snapshotId: $snapshotId)
  }
`;

// Types
export interface AuthFailure {
  roleId: string;
  message: string;
}

export interface AuthHealthStatus {
  passed: boolean;
  totalAssertions: number;
  failures: AuthFailure[];
  checkedAt: string;
}

export interface DriftItem {
  roleId: string;
  scope: string;
}

export interface AuthDriftResult {
  hasDrift: boolean;
  baselinePath: string;
  removed: DriftItem[];
  added: DriftItem[];
  changed: string[];
}

export interface CapabilityCheck {
  subgraph: string;
  entity: string;
  shouldHaveAccess: boolean;
  passed: boolean;
}

export interface LoginSimulationResult {
  email: string;
  roleId: string;
  tenantSlug: string;
  tenantNum: number;
  baseScopeCount: number;
  overrideScopeCount: number;
  overrideId: string | null;
  totalScopeCount: number;
  effectiveScopes: string[];
  capabilityChecks: CapabilityCheck[];
  allPassed: boolean;
}

export interface Persona {
  email: string;
  role: string;
  tenant: string;
}

export interface AuthSnapshot {
  snapshotId: string;
  generatedAt: string;
  gitCommit: string;
  roleCount: number;
  scopeRuleCount: number;
  roleOverrideCount: number;
  path: string;
}

/**
 * Hook for auth health check
 */
export function useAuthHealthCheck() {
  const { data, loading, error, refetch } = useQuery<{ authHealthCheck: AuthHealthStatus }>(
    AUTH_HEALTH_CHECK,
    { fetchPolicy: 'network-only' }
  );

  return {
    healthStatus: data?.authHealthCheck,
    loading,
    error,
    refetch,
  };
}

/**
 * Hook for auth drift detection
 */
export function useAuthDriftStatus() {
  const { data, loading, error, refetch } = useQuery<{ authDriftStatus: AuthDriftResult }>(
    AUTH_DRIFT_STATUS,
    { fetchPolicy: 'network-only' }
  );

  return {
    driftStatus: data?.authDriftStatus,
    loading,
    error,
    refetch,
  };
}

/**
 * Hook for login simulation
 */
export function useSimulateLogin(email: string | null) {
  const { data, loading, error, refetch } = useQuery<{ simulateLogin: LoginSimulationResult }>(
    SIMULATE_LOGIN,
    {
      variables: { email },
      skip: !email,
      fetchPolicy: 'network-only',
    }
  );

  return {
    simulation: data?.simulateLogin,
    loading,
    error,
    refetch,
  };
}

/**
 * Hook for listing personas
 */
export function useListPersonas() {
  const { data, loading, error } = useQuery<{ listPersonas: Persona[] }>(LIST_PERSONAS);

  return {
    personas: data?.listPersonas ?? [],
    loading,
    error,
  };
}

/**
 * Hook for generating auth snapshot
 */
export function useGenerateSnapshot() {
  const [generate, { data, loading, error }] = useMutation<{ generateAuthSnapshot: AuthSnapshot }>(
    GENERATE_SNAPSHOT
  );

  return {
    generateSnapshot: generate,
    snapshot: data?.generateAuthSnapshot,
    loading,
    error,
  };
}

/**
 * Hook for setting auth baseline
 */
export function useSetBaseline() {
  const [setBaseline, { data, loading, error }] = useMutation<{ setAuthBaseline: boolean }>(
    SET_BASELINE
  );

  return {
    setBaseline: (snapshotId: string) => setBaseline({ variables: { snapshotId } }),
    success: data?.setAuthBaseline,
    loading,
    error,
  };
}

/**
 * Combined hook for auth observability dashboard
 */
export function useAuthObservability() {
  const health = useAuthHealthCheck();
  const drift = useAuthDriftStatus();
  const personas = useListPersonas();
  const snapshot = useGenerateSnapshot();
  const baseline = useSetBaseline();

  return {
    health,
    drift,
    personas,
    snapshot,
    baseline,
    refetchAll: () => {
      health.refetch();
      drift.refetch();
    },
  };
}
